import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Pencil, Plus, Star, Trash2, Users } from "lucide-react";
import type { Tables } from "@/integrations/supabase/types";

type Customer = Tables<"customers">;
type Contact = Tables<"customer_contacts">;

const emptyForm = { company: "", contact_name: "", email: "", address: "" };
const emptyContact = { id: "", name: "", email: "", phone: "", role_title: "", is_primary: false };

const AdminCustomers = () => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Customer | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const [contactCustomer, setContactCustomer] = useState<Customer | null>(null);
  const [contactForm, setContactForm] = useState(emptyContact);
  const [contactSaving, setContactSaving] = useState(false);

  const load = async () => {
    const [{ data, error }, { data: cc }] = await Promise.all([
      supabase.from("customers").select("*").order("company"),
      supabase.from("customer_contacts").select("*").order("name"),
    ]);
    if (error) toast.error("Kunden konnten nicht geladen werden");
    else setCustomers(data ?? []);
    setContacts(cc ?? []);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const contactsOf = (customerId: string) => contacts.filter((c) => c.customer_id === customerId);

  const openNew = () => {
    setEditing(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (c: Customer) => {
    setEditing(c);
    setForm({
      company: c.company,
      contact_name: c.contact_name ?? "",
      email: c.email ?? "",
      address: c.address ?? "",
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.company.trim()) {
      toast.error("Bitte geben Sie eine Firma an.");
      return;
    }
    setSaving(true);
    const payload = {
      company: form.company.trim(),
      contact_name: form.contact_name.trim() || null,
      email: form.email.trim() || null,
      address: form.address.trim() || null,
    };
    const { error } = editing
      ? await supabase.from("customers").update(payload).eq("id", editing.id)
      : await supabase.from("customers").insert(payload);
    setSaving(false);
    if (error) {
      toast.error("Speichern fehlgeschlagen");
      return;
    }
    toast.success("Kunde gespeichert");
    setDialogOpen(false);
    load();
  };

  const remove = async (c: Customer) => {
    if (!window.confirm(`Kunde „${c.company}" wirklich löschen? Zugehörige Projekte, Aufträge und Leistungsnachweise werden ebenfalls gelöscht.`)) return;
    const { error } = await supabase.from("customers").delete().eq("id", c.id);
    if (error) toast.error("Löschen fehlgeschlagen");
    else {
      toast.success("Kunde gelöscht");
      load();
    }
  };

  const saveContact = async () => {
    if (!contactCustomer || !contactForm.name.trim()) {
      toast.error("Bitte geben Sie einen Namen an.");
      return;
    }
    setContactSaving(true);
    const payload = {
      customer_id: contactCustomer.id,
      name: contactForm.name.trim(),
      email: contactForm.email.trim() || null,
      phone: contactForm.phone.trim() || null,
      role_title: contactForm.role_title.trim() || null,
      is_primary: contactForm.is_primary,
    };
    const { error } = contactForm.id
      ? await supabase.from("customer_contacts").update(payload).eq("id", contactForm.id)
      : await supabase.from("customer_contacts").insert(payload);
    if (!error && contactForm.is_primary) {
      const query = supabase
        .from("customer_contacts")
        .update({ is_primary: false })
        .eq("customer_id", contactCustomer.id);
      if (contactForm.id) await query.neq("id", contactForm.id);
      else await query.neq("name", payload.name);
    }
    setContactSaving(false);
    if (error) {
      toast.error("Ansprechpartner konnte nicht gespeichert werden");
      return;
    }
    toast.success("Ansprechpartner gespeichert");
    setContactForm(emptyContact);
    load();
  };

  const removeContact = async (contact: Contact) => {
    if (!window.confirm(`Ansprechpartner „${contact.name}" wirklich löschen?`)) return;
    const { error } = await supabase.from("customer_contacts").delete().eq("id", contact.id);
    if (error) toast.error("Löschen fehlgeschlagen");
    else {
      toast.success("Ansprechpartner gelöscht");
      load();
    }
  };

  return (
    <>
      <Helmet>
        <title>Kunden | Leistungsnachweise</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold tracking-tight">Kunden</h1>
        <Button className="gap-2" onClick={openNew}>
          <Plus className="h-4 w-4" /> Neuer Kunde
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Firma</TableHead>
                <TableHead className="hidden md:table-cell">Ansprechpartner</TableHead>
                <TableHead className="hidden md:table-cell">E-Mail</TableHead>
                <TableHead className="w-36 text-right">Aktionen</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={4} className="py-8 text-center text-muted-foreground">Laden…</TableCell></TableRow>
              ) : customers.length === 0 ? (
                <TableRow><TableCell colSpan={4} className="py-8 text-center text-muted-foreground">Noch keine Kunden angelegt.</TableCell></TableRow>
              ) : (
                customers.map((c) => {
                  const list = contactsOf(c.id);
                  return (
                    <TableRow key={c.id}>
                      <TableCell className="font-medium">{c.company}</TableCell>
                      <TableCell className="hidden md:table-cell">
                        {list.length === 0 ? "–" : (
                          <div className="flex flex-wrap gap-1">
                            {list.map((p) => (
                              <Badge key={p.id} variant={p.is_primary ? "default" : "secondary"}>{p.name}</Badge>
                            ))}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="hidden md:table-cell">{c.email ?? "–"}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => { setContactCustomer(c); setContactForm(emptyContact); }}
                          aria-label="Ansprechpartner verwalten"
                        >
                          <Users className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => openEdit(c)} aria-label="Bearbeiten">
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => remove(c)} aria-label="Löschen">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? "Kunde bearbeiten" : "Neuer Kunde"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="company">Firma *</Label>
              <Input id="company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="contact">Allgemeiner Kontakt (optional)</Label>
              <Input id="contact" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cust-email">Allgemeine E-Mail (Rückfalladresse für Freigabe-Links)</Label>
              <Input id="cust-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="address">Adresse</Label>
              <Textarea id="address" rows={3} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Abbrechen</Button>
            <Button onClick={save} disabled={saving}>{saving ? "Speichern…" : "Speichern"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(contactCustomer)} onOpenChange={(open) => { if (!open) setContactCustomer(null); }}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Ansprechpartner · {contactCustomer?.company}</DialogTitle>
          </DialogHeader>

          <div className="space-y-2">
            {contactCustomer && contactsOf(contactCustomer.id).length === 0 && (
              <p className="text-sm text-muted-foreground">Noch keine Ansprechpartner hinterlegt.</p>
            )}
            {contactCustomer && contactsOf(contactCustomer.id).map((p) => (
              <div key={p.id} className="flex flex-wrap items-center gap-2 rounded-md border p-3 text-sm">
                <div className="min-w-0 flex-1">
                  <p className="flex items-center gap-2 font-medium">
                    {p.name}
                    {p.is_primary && <Star className="h-3.5 w-3.5 text-primary" aria-label="Hauptansprechpartner" />}
                  </p>
                  <p className="text-muted-foreground">
                    {[p.role_title, p.email, p.phone].filter(Boolean).join(" · ") || "Keine weiteren Angaben"}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="Bearbeiten"
                  onClick={() => setContactForm({
                    id: p.id,
                    name: p.name,
                    email: p.email ?? "",
                    phone: p.phone ?? "",
                    role_title: p.role_title ?? "",
                    is_primary: p.is_primary,
                  })}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon" aria-label="Löschen" onClick={() => removeContact(p)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>

          <div className="space-y-4 border-t pt-4">
            <p className="font-medium">{contactForm.id ? "Ansprechpartner bearbeiten" : "Neuer Ansprechpartner"}</p>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="ct-name">Name *</Label>
                <Input id="ct-name" value={contactForm.name} onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="ct-role">Funktion</Label>
                <Input id="ct-role" value={contactForm.role_title} onChange={(e) => setContactForm({ ...contactForm, role_title: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="ct-email">E-Mail</Label>
                <Input id="ct-email" type="email" value={contactForm.email} onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="ct-phone">Telefon</Label>
                <Input id="ct-phone" value={contactForm.phone} onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })} />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="h-4 w-4"
                checked={contactForm.is_primary}
                onChange={(e) => setContactForm({ ...contactForm, is_primary: e.target.checked })}
              />
              Als Hauptansprechpartner festlegen
            </label>
            <div className="flex justify-end gap-2">
              {contactForm.id && (
                <Button variant="outline" onClick={() => setContactForm(emptyContact)}>Abbrechen</Button>
              )}
              <Button onClick={saveContact} disabled={contactSaving}>
                {contactSaving ? "Speichern…" : contactForm.id ? "Änderungen speichern" : "Ansprechpartner hinzufügen"}
              </Button>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setContactCustomer(null)}>Schließen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AdminCustomers;
